import { createHeaders, useServerState } from "@project-mgmt/api-client";
// API endpoints for workspace operations
const WORKSPACE_ENDPOINTS = {
    list: "/api/workspaces",
    create: "/api/workspaces",
    get: (id) => `/api/workspaces/${id}`,
    update: (id) => `/api/workspaces/${id}`,
    delete: (id) => `/api/workspaces/${id}`,
    invite: (id) => `/api/workspaces/${id}/invites`,
    join: (id) => `/api/workspaces/${id}/join`,
    updateMemberRole: (workspaceId, memberId) => `/api/workspaces/${workspaceId}/members/${memberId}/role`,
    removeMember: (workspaceId, memberId) => `/api/workspaces/${workspaceId}/members/${memberId}`,
};
/**
 * Hook to fetch all workspaces for the current user
 *
 */
export function useWorkspaces() {
    return useServerState({
        key: ["workspaces"],
        fetcher: async (signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.list, {
                headers: createHeaders(),
                signal,
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch workspaces: ${response.statusText}`);
            }
            return response.json();
        },
    });
}
/**
 * Hook to fetch workspace details with members
 */
export function useWorkspaceDetails(workspaceId) {
    return useServerState({
        key: ["workspaces", workspaceId],
        fetcher: async (signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.get(workspaceId), {
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch workspace: ${response.statusText}`);
            }
            return response.json();
        },
        enabled: !!workspaceId,
    });
}
/**
 * Hook to create a new workspace
 */
export function useCreateWorkspace() {
    return useServerState({
        key: ["workspaces", "create"],
        fetcher: async (data, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.create, {
                method: "POST",
                headers: createHeaders(),
                body: JSON.stringify(data),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to create workspace: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to update workspace details
 */
export function useUpdateWorkspace(workspaceId) {
    return useServerState({
        key: ["workspaces", workspaceId, "update"],
        fetcher: async (data, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.update(workspaceId), {
                method: "PUT",
                headers: createHeaders(workspaceId),
                body: JSON.stringify(data),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to update workspace: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to delete a workspace
 */
export function useDeleteWorkspace() {
    return useServerState({
        key: ["workspaces", "delete"],
        fetcher: async (workspaceId, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.delete(workspaceId), {
                method: "DELETE",
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to delete workspace: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to invite a user to workspace
 */
export function useInviteUser(workspaceId) {
    return useServerState({
        key: ["workspaces", workspaceId, "invite"],
        fetcher: async (data, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.invite(workspaceId), {
                method: "POST",
                headers: createHeaders(workspaceId),
                body: JSON.stringify(data),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to invite user: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to join a workspace (accept invitation)
 */
export function useJoinWorkspace() {
    return useServerState({
        key: ["workspaces", "join"],
        fetcher: async (workspaceId, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.join(workspaceId), {
                method: "POST",
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to join workspace: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to update member role
 */
export function useUpdateMemberRole(workspaceId) {
    return useServerState({
        key: ["workspaces", workspaceId, "updateMemberRole"],
        fetcher: async ({ memberId, role }, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.updateMemberRole(workspaceId, memberId), {
                method: "PUT",
                headers: createHeaders(workspaceId),
                body: JSON.stringify({ role }),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to update member role: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
/**
 * Hook to remove member from workspace
 */
export function useRemoveMember(workspaceId) {
    return useServerState({
        key: ["workspaces", workspaceId, "removeMember"],
        fetcher: async (memberId, signal) => {
            const response = await fetch(WORKSPACE_ENDPOINTS.removeMember(workspaceId, memberId), {
                method: "DELETE",
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || `Failed to remove member: ${response.statusText}`);
            }
            return response.json();
        },
        manual: true,
    });
}
